import {
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select
} from "@mui/material";
import classNames from "classnames";
import { nanoid } from "nanoid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ExploreVector } from "../core/global";
import styles from "../styles/components/explore-select.module.scss";
import { StyledMarkdown } from "./typeface";

type Columns = (string | ExploreVector | { key: string, title: string, group?: string, groupTitle?: string, index?: number })[]
interface ExploreTimeseriesPlotProps {
  title?: string
  columnKeys: Columns
  onChangeKey: (key: string[] | null) => void
  initialValue?: number
  disabled?: boolean
  className?: string
  valueIndex?: number | null
  onClick?: (index: number | null) => void
}

export const ExploreSelect = ({
  title,
  columnKeys,
  onChangeKey,
  initialValue,
  disabled = false,
  className,
  valueIndex,
  onClick,
}: ExploreTimeseriesPlotProps) => {
  const [selectedMatrix, setSelectedMatrix] = useState<number | null>(initialValue || 0);
  const [id, setId] = useState<string>('');

  const useSelectedMatrix = valueIndex !== undefined && valueIndex !== null ? valueIndex : selectedMatrix

  useEffect(() => {
    setSelectedMatrix(initialValue || 0)
    setId(nanoid())
  }, [initialValue])
  
  useEffect(() => {
    if (useSelectedMatrix !== null && columnKeys.length > 0) {
      const useKey = columnKeys[useSelectedMatrix]
      if (useKey && typeof useKey !== "string" && Object.prototype.hasOwnProperty.call(useKey, 'key')) {
        onChangeKey([useKey.key])
      } else {
        onChangeKey([useKey as string])
      }
      
    }
  }, [useSelectedMatrix, columnKeys]);

  const currentValue = useMemo(() => {
    if (useSelectedMatrix === null || columnKeys.length === 0) {
      return ''
    }
    const value = columnKeys[useSelectedMatrix]
    if (value && typeof value !== "string" && Object.prototype.hasOwnProperty.call(value, 'key')) {
      return value.key
    }
    return value
    
  }, [useSelectedMatrix, columnKeys])

  const {groups, order} = useMemo(() => {
    const g: { [key: string]: { title: string, items: (string | ExploreVector | { key: string, title: string, group?: string, groupTitle?: string, index?: number })[] } } = {}
    columnKeys.forEach((item, index) => {
      if (typeof item === "object" 
        && Object.prototype.hasOwnProperty.call(item, 'group') 
        && Object.prototype.hasOwnProperty.call(item, 'groupTitle')
      ) {
        // resolve group key and title
        // @ts-ignore
        const {group, groupTitle} = item
        const groupKey = group || '_default'
        if (!g[groupKey]) {
          g[groupKey] = {
            title: groupTitle || groupKey,
            items: []
          }
        }
        g[groupKey].items.push({
          ...item,
          index
        })
      } else {
        if (!g['_default']) {
          g['_default'] = {
            title: '',
            items: []
          }
        }
        if (typeof item === "object") {
          g['_default'].items.push({
            ...item,
            index
          })
        } else {
          g['_default'].items.push(item)
        }
      }
    })

    Object.keys(g).forEach((groupKey) => {
      g[groupKey].items.sort((a, b) => {
        const aTitle = typeof a === "string" ? a : (Object.prototype.hasOwnProperty.call(a, 'title') ? (a as { title: string }).title : '')
        const bTitle = typeof b === "string" ? b : (Object.prototype.hasOwnProperty.call(b, 'title') ? (b as { title: string }).title : '')
        return aTitle.localeCompare(bTitle)
      })
    })
    if (Object.keys(g).length === 1 && Object.keys(g)[0] === '_default') {
      return {
        groups: {}, 
        order: []
      }
    }
    // alphabetical sort
    return {
      groups: g, 
      order: Object.keys(g).sort()
    }
  }, [columnKeys])

  const getTitleKeyIndex = useCallback((item: string | ExploreVector | { key: string, title: string, group?: string, groupTitle?: string, index?: number }, index: number): { title: string, key: string, index: number | null } => { 
    let key = item
    let title = ''
    let _index = index
    if (typeof item !== "string") {
      if (item && Object.prototype.hasOwnProperty.call(item, 'key')) {
        key = item.key as string
      }
      if (item && Object.prototype.hasOwnProperty.call(item, 'title')) {
        title = (item as { title: string }).title;
      }
      if (item && Object.prototype.hasOwnProperty.call(item, 'index')) {
        _index = (item as { index: number }).index;
      }
    }
    title = `${title ? title : key}`
    return {
      title,
      key: key as string,
      index: typeof _index === "number" ? _index : null
    }
  }, [])

  return (
    <>
      <FormControl className={classNames(className)}>
        {title && <InputLabel id={id}>{title}</InputLabel>}
        <Select
          disabled={disabled}
          label={title}
          size="small"
          style={{ width: "180px", marginRight: "8px" }}
          labelId={id}
          id={id}
          value={currentValue}
        >
        {groups && Object.keys(groups).length > 0 &&
          order.flatMap((groupKey, groupIndex) => {
            const item = groups[groupKey]
            return [
                <ListSubheader 
                  key={`${groupIndex}-${item.title}`}
                  className={styles.selectTitle}>
                    {`${item.title}`}
                </ListSubheader>,
                item.items.map((item: any, _index: number) => {
                  const {key, title, index} = getTitleKeyIndex(item, _index)
                  return (
                    <MenuItem
                      disabled={title.startsWith('~~')}   // this is crossed out items
                      key={`${index}-${key}`}
                      value={key as string}
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        if (index !== null) {
                          if (onClick) {
                            onClick(index)
                          }
                          setSelectedMatrix(index)
                        }
                      }}
                    >
                      <StyledMarkdown className={styles.item}>
                        {title}
                      </StyledMarkdown>
                    </MenuItem>
                  );
                })
              ]
            })
          }
          {Object.keys(groups).length === 0 && columnKeys.length > 0 &&
            columnKeys.map((item: any, _index: number) => {
              const {key, title, index} = getTitleKeyIndex(item, _index)
              return (
                <MenuItem
                  disabled={title.startsWith('~~')}   // this is crossed out items
                  key={`${index}-${key}`}
                  value={key as string}
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    if (index !== null) {
                      if (onClick) {
                        onClick(index)
                      }
                      setSelectedMatrix(index)
                    }
                  }}
                >
                  <StyledMarkdown className={styles.item}>
                    {title}
                  </StyledMarkdown>
                </MenuItem>
              );
            })
          }
        </Select>
      </FormControl>
    </>
  )
}
